import React, { useCallback, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';

import { formatCurrencyAsText } from '~/utils/functions';
import { useEmissaoNFE } from '~/pages/EmissaoNFE/EmissaoNFEContext';
import { InputTextArea, InputSelect } from '~/components/NovosInputs';
import api from '~/services/api';
import {
  InfoComplementarNFE,
  ResponseInfoComplementar,
  SeletorInfo,
} from '~/pages/EntradaNFE/protocols/InformacaoComplementar';
import { useQueryClient } from 'react-query';

export type SelectType = {
  value: number | string;
  label: string;
};

export const FormFinanceiro: React.FC = () => {
  const [categorias, setCategorias] = useState<SelectType[]>([]);
  const [contas, setContas] = useState<SelectType[]>([]);
  const [infos, setInfos] = useState<SeletorInfo[]>([]);
  const [allInfos, setAllInfos] = useState<InfoComplementarNFE[]>([]);

  const {
    codLoja,
    formNFE: {
      watch: watchValuesNFe,
      register: registerNFe,
      control: controlNFe,
      setValue: setValueNFe,
    },
    formFinanceiro: {
      register,
      control,
      setValue,
      formState: formStateFinanceiro,
      clearErrors: clearErrorsFormFinanceiro,
    },
    parcelasFinanceiro,
    totalFinanceiro,
    isUpdate,
    setTotalFinanceiro,
  } = useEmissaoNFE();
  const perfil = watchValuesNFe('cod_perfil');
  const queryClient = useQueryClient();

  // Categorias
  useEffect(() => {
    (async () => {
      if (perfil) {
        const tipo_categoria = perfil.tipo_operacao === 1 ? 0 : 1;
        const { data } = await api.get(
          `/perfil-loja/categorias/${tipo_categoria}`,
        );
        const option = data.data.map((categoria: any) => {
          return {
            value: categoria.cod_categoria,
            label: categoria.descricao,
            ...categoria,
          };
        });

        setCategorias(option);
      }
    })();
  }, [perfil]);

  // Contas
  useEffect(() => {
    (async () => {
      if (codLoja) {
        const { data } = await api.get(`/contas/loja/${codLoja}`);
        const option = data.data.map((conta: any) => {
          return {
            value: conta.cod_cc,
            label: conta.descricao,
            ...conta,
          };
        });

        setContas(option);
      }
    })();
  }, [codLoja]);

  const getInfoComplementar = useCallback(async () => {
    const { data } = await api.get<ResponseInfoComplementar>(
      '/entrada-nfe/informacao/complementar',
    );

    if (data.success) {
      let options: SeletorInfo[] = data.data.map((opt) => ({
        label: `${opt.cod_info_complementar} - ${opt.des_titulo}`,
        value: opt.cod_info_complementar,
      }));

      options = options.sort((a, b) => Number(a.value) - Number(b.value));

      const newOptions: SeletorInfo[] = [
        { label: 'Nenhuma', value: null },
        ...options,
      ];

      setInfos(newOptions);
      setAllInfos(data.data);
    } else {
      setInfos([{ label: 'Nenhuma', value: null }]);
      setAllInfos([]);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const codTela: any = queryClient.getQueryData(`cod_tela_active`);
      if (codTela) {
        if (codTela.cod_tela === 47) {
          getInfoComplementar();
        }
      }
    })();
  }, [queryClient.getQueryData(`cod_tela_active`)]);

  useEffect(() => {
    getInfoComplementar();
  }, []);

  useEffect(() => {
    let totalParcelas = 0;
    parcelasFinanceiro.map((parcela) => {
      totalParcelas += parcela.val_parcela || 0;
      return parcela;
    });
    setTotalFinanceiro(totalParcelas);
  }, [parcelasFinanceiro, setTotalFinanceiro]);

  return (
    <>
      <Row className="d-flex align-items-end justify-content-between">
        <Col sm={12} md={3}>
          <InputSelect
            ListaWidth="580px"
            label="Categoria"
            placeholder="Selecione..."
            name="categoria"
            register={register}
            isDisabled={isUpdate}
            isError={!!formStateFinanceiro.errors.categoria}
            control={control}
            options={categorias}
            changeSelected={(selected) => {
              clearErrorsFormFinanceiro('categoria');
              setValue('categoria', selected);
            }}
          />
        </Col>
        <Col sm={12} md={3}>
          <InputSelect
            ListaWidth="480px"
            label="Conta"
            placeholder="Selecione..."
            name="conta"
            register={register}
            isDisabled={isUpdate}
            isError={!!formStateFinanceiro.errors.conta}
            control={control}
            options={contas}
            changeSelected={(selected) => {
              clearErrorsFormFinanceiro('conta');
              setValue('conta', selected);
            }}
          />
        </Col>
        <Col sm={12} md={6}>
          <h5
            style={{
              textAlign: 'right',
              display: 'block',
              marginTop: '20px',
            }}
          >
            {`Total Financeiro: R$${formatCurrencyAsText(
              totalFinanceiro || 0,
            )}`}
          </h5>
        </Col>
        <Col md={3} sm={12}>
          <InputSelect
            label="Informação Complementar"
            placeholder="Nenhuma"
            name="info_complementar"
            register={registerNFe}
            isError={false}
            disabled={isUpdate}
            control={controlNFe}
            options={infos as SelectType[]}
            changeSelected={(selected) => {
              setValueNFe('info_complementar', selected);

              if (!selected.value) setValueNFe('des_info_complementar', '');
              else {
                const info = allInfos.find(
                  (i) =>
                    Number(i.cod_info_complementar) === Number(selected.value),
                );

                if (info)
                  setValueNFe('des_info_complementar', info.des_informacao);
                else setValueNFe('des_info_complementar', '');
              }
            }}
          />
        </Col>
        <Col md={12} sm={12}>
          <InputTextArea
            maxLength={500}
            label="Informação Complementar"
            name="des_info_complementar"
            register={registerNFe}
            disabled={isUpdate}
            control={controlNFe}
            caseInput="none"
          />
        </Col>
      </Row>
    </>
  );
};
